import React, { ReactNode, useCallback, useState } from 'react';
import { useQuery } from '@apollo/client';
import { subMinutes } from 'date-fns';

import { useInterval } from '@trqk/hooks';
import { queryStrings } from '@trqk/resolvers';
import { getInLocalStorage, setInLocalStorage } from '@trqk/utils';

import { useModal } from '../../../hooks';
import { LOCAL_STORAGE_KEYS } from '../../../constants';
import { ScriptBlockerWarning } from '.';

const { GET_SESSION_CURRENT_USER } = queryStrings;

const FIVE_MINUTES = 1000 * 60 * 5;

interface CheckForScriptBlockerProps {
    children: ReactNode;
}

const getLocalStorageKey = (userId: Maybe<string>): Maybe<string> => {
    if (userId === null) {
        return null;
    }

    return `didSilenceScriptBlockerWarning.user:${userId}`;
};

/**
 * If we detect that a user's environment is blocking our network calls, show the user a message
 * that warns them about it and suggests that they add an exception if they are using antivirus
 * software or browser extensions that are blocking our app.
 * @param props - see {@link PaymentPlanStatusProviderProps}
 */
export const CheckForScriptBlocker = ({
    children,
}: CheckForScriptBlockerProps): JSX.Element => {
    const modal = useModal();
    const [modalId, setModalId] = useState<Maybe<string>>(null);
    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const userId = currentUserQuery?.data?.session?.currentUser?.id ?? null;
    const didSilenceWarningLocalStorageKey = getLocalStorageKey(userId);

    /**
     * Check for script blocker flags in localStorage. Flags are stored within an object with the
     * shape:
     *  {
     *      [callSiteId]: timestamp
     *  }
     *
     * where `callSiteId` identifies a code location that makes a network call, and `timestamp`
     * is the number of milliseconds since the epoch indicating when the last network call was made
     * for that call site.
     *
     * If any flag is found within this object that is more than 5 minutes old, and the user has
     * not previously silenced the warning, show a modal that warns the user about the issue. This
     * means that the call site must take care of clearing out this ID upon a network response
     * (whether it was successful or not).
     */
    const checkForFlags = useCallback(() => {
        const didSilenceWarning =
            didSilenceWarningLocalStorageKey === null
                ? false
                : getInLocalStorage<boolean>(
                      didSilenceWarningLocalStorageKey,
                  ) ?? false;

        const flags = getInLocalStorage<Record<string, number>>(
            LOCAL_STORAGE_KEYS.SCRIPT_BLOCKER_FLAGS,
        );

        const now = new Date();
        const twoMinutesAgo = subMinutes(now, 2);

        if (flags !== null) {
            let foundOldFlag = false;
            const nextFlags: Record<string, number> = {};

            Object.entries(flags).forEach(([key, value]): void => {
                const flagDate = new Date(value);

                if (flagDate < twoMinutesAgo) {
                    foundOldFlag = true;
                } else {
                    // Only include flags in the next state if they are not over two minutes old
                    nextFlags[key] = value;
                }
            });

            setInLocalStorage(
                LOCAL_STORAGE_KEYS.SCRIPT_BLOCKER_FLAGS,
                (): Record<string, number> => nextFlags,
            );

            if (foundOldFlag && !didSilenceWarning && modalId === null) {
                const nextModalId = modal.add({
                    Content: ScriptBlockerWarning,
                    input: {
                        localStorageKey: didSilenceWarningLocalStorageKey,
                    },
                });

                setModalId(nextModalId);
            }
        }
    }, [didSilenceWarningLocalStorageKey, modalId]);

    useInterval(checkForFlags, FIVE_MINUTES);

    return <>{children}</>;
};
